
import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { debugLogger } from '@/utils/debugLogger';

interface UserPresenceProps {
  presenceStatus?: 'online' | 'typing' | 'recording audio' | 'offline';
  lastSeen?: string | null;
  mostRecentActivity?: string | null;
  className?: string;
}

export const UserPresence: React.FC<UserPresenceProps> = ({
  presenceStatus = 'offline',
  lastSeen,
  mostRecentActivity,
  className = ''
}) => {
  const getPresenceText = () => {
    switch (presenceStatus) {
      case 'online':
        return 'online';
      case 'typing':
        return 'digitando...';
      case 'recording audio':
        return 'gravando áudio...';
      case 'offline':
      default:
        // Para offline, mostrar tempo baseado na atividade mais recente
        const timeToUse = mostRecentActivity || lastSeen;
        
        if (timeToUse) {
          try {
            const date = new Date(timeToUse);
            const now = new Date();
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
              return 'offline';
            }
            
            const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
            
            if (diffInMinutes < 1) {
              return 'agora mesmo';
            } else if (diffInMinutes < 60) {
              return `há ${diffInMinutes} min`;
            } else if (diffInMinutes < 1440) { // Less than 24 hours
              const hours = Math.floor(diffInMinutes / 60);
              return `há ${hours}h`;
            } else {
              return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
            }
          } catch (error) {
            debugLogger.error('Error formatting date:', error, { timeToUse });
            return 'offline';
          }
        }
        
        return 'offline';
    }
  };

  const getPresenceColor = () => {
    switch (presenceStatus) {
      case 'online':
        return 'text-green-400';
      case 'typing':
        return 'text-blue-400';
      case 'recording audio':
        return 'text-orange-400';
      case 'offline':
      default:
        return 'text-white/50';
    }
  };

  const getPresenceIcon = () => {
    switch (presenceStatus) {
      case 'online':
        return '●';
      case 'typing':
        return '✏️';
      case 'recording audio':
        return '🎤';
      case 'offline':
      default:
        return '○';
    }
  };

  return (
    <span className={`text-xs flex items-center gap-1 ${getPresenceColor()} ${className}`}>
      <span>{getPresenceIcon()}</span>
      {getPresenceText()}
    </span>
  );
};
